/*
  Mon Chou — coordinates map (2026-08-04, Claude)

  Replaces adev.map() from assets/adev.js for the "Koordinater" engraving option.

  ---------------------------------------------------------------------------
  1. The map used to be built in a ZERO-SIZE container
  ---------------------------------------------------------------------------
  layout/theme.liquid lazy-loads the Maps API on the FIRST pointerover / touchstart
  / click / focus of the Koordinater pill. On desktop that means merely HOVERING the
  pill loads Maps and calls adev.map() — while the coordinates panel is still
  display:none and the container measures 0x0.

  A google.maps.Map constructed at 0x0 never finishes its first layout: it paints a
  stale, partial tile set (the map looks washed out / half drawn) and its control
  layer is never sized, so map.controls[...].push() never re-parents the address
  search field into the map. Opening the panel afterwards does not fix it, because
  nothing tells the map to re-measure.

  So: if the container has no size yet, do NOT build. Park it back on the shimmer
  placeholder and build the moment it actually gets a size. A ResizeObserver on the
  built map then keeps it correct across hide/show of the tab and window resizes.

  ---------------------------------------------------------------------------
  2. There was no marker, and the "glitch" while panning
  ---------------------------------------------------------------------------
  The original drew no marker. The selected point was a google.maps.InfoWindow whose
  content was JSON.stringify(latLng) — a bubble reading { "lat": 55.679…, "lng":
  12.569… } over the middle of the map — and it was closed and RE-CREATED on every
  "bounds_changed", which fires continuously while dragging. That was the flicker.

  Now: a red Google-style pin fixed at the centre of the map (assets/mc-map.css).
  The coordinate has always been the map centre, so a centre pin is what actually
  marks the selected point — and being a static overlay rather than a marker
  re-positioned every frame, it cannot judder while you pan. Clicking pans to the
  clicked point so pin and coordinate never disagree. Coordinates are written on
  "idle" instead of on every bounds_changed frame.

  Behaviour deliberately preserved: same default centre and zoom, same controls,
  same Places search box. Coordinates still follow the map centre and are still
  written through adev.change_input() — always resolved at call time, never cached,
  because layout/theme.liquid swaps in a wrapper that suppresses writes until the
  customer actually touches the map.

  Loaded from snippets/product-personalizer.liquid (product templates only).
*/
(function () {
  'use strict';

  /* Same default centre + zoom the theme has always used. */
  var START = { lat: 55.679140329589515, lng: 12.56900668045048 };
  var ZOOM = 16;

  var PIN_SVG =
    '<svg viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M12 0C5.37 0 0 5.37 0 12c0 8.4 10.05 22.2 11.05 23.55a1.18 1.18 0 0 0 1.9 0C13.95 34.2 24 20.4 24 12c0-6.63-5.37-12-12-12z" fill="#EA4335"/>' +
    '<circle cx="12" cy="12" r="4.6" fill="#A52714"/>' +
    '</svg>';

  function widthOf(el) {
    return el.getBoundingClientRect().width;
  }

  function makeEl(cls, html) {
    var el = document.createElement('div');
    el.className = cls;
    if (html) { el.innerHTML = html; }
    return el;
  }

  /*
    Always look adev.change_input up on the object: layout/theme.liquid swaps in a
    suppressing wrapper before calling adev.map(), and swaps the real one back once
    the customer interacts with the map. Caching the reference would defeat that.
  */
  function writeCoords(lat, lng) {
    if (!window.adev || typeof window.adev.change_input !== 'function') { return; }
    if (typeof window.convertLatLngToDMS !== 'function') { return; }
    window.adev.change_input(window.convertLatLngToDMS(lat, lng));
  }

  /* Run cb once the element actually occupies space. */
  function whenSized(el, cb) {
    var done = false;
    var ro = null;
    var poll = null;

    function stop() {
      if (ro) { ro.disconnect(); ro = null; }
      if (poll) { window.clearInterval(poll); poll = null; }
    }
    function check() {
      if (done || widthOf(el) <= 0) { return; }
      done = true;
      stop();
      cb();
    }

    if (window.ResizeObserver) {
      ro = new window.ResizeObserver(check);
      ro.observe(el);
    }
    /* Safety net for browsers without ResizeObserver; also self-limiting. */
    var ticks = 0;
    poll = window.setInterval(function () {
      ticks++;
      if (ticks > 2400) { stop(); return; }   /* ~10 min, then give up quietly */
      check();
    }, 250);

    check();
  }

  function build(mapEl) {
    var map = new google.maps.Map(mapEl, { zoom: ZOOM, center: START });
    /* adev.js kept the map on an implicit global; keep it for anything that looked there. */
    window.map = map;

    /*
      Appended AFTER the map is constructed so the Maps API does not clear them,
      and positioned by assets/mc-map.css relative to .adev-map (the API sets
      position:relative on that element itself).
    */
    var pin = makeEl('mc-map-pin',
      '<span class="mc-map-pin__shadow"></span>' +
      '<span class="mc-map-pin__svg">' + PIN_SVG + '</span>');
    var shimmer = makeEl('mc-map-shimmer');
    mapEl.appendChild(pin);
    mapEl.appendChild(shimmer);

    var revealed = false;
    function reveal() {
      if (revealed) { return; }
      revealed = true;
      mapEl.classList.add('mc-map--ready');
      shimmer.classList.add('mc-map-shimmer--done');
      window.setTimeout(function () {
        if (shimmer.parentNode) { shimmer.parentNode.removeChild(shimmer); }
      }, 500);
    }
    /*
      Three ways out, because "tilesloaded" is not guaranteed — it never fires if
      tile requests fail. "idle" is far more reliable, and the timeout is a hard
      backstop so the shimmer can never strand the map behind a grey panel.
    */
    google.maps.event.addListenerOnce(map, 'tilesloaded', reveal);
    google.maps.event.addListenerOnce(map, 'idle', reveal);
    window.setTimeout(reveal, 1500);

    /*
      Keep the map correct if the panel is closed and re-opened, or the window is
      resized/rotated: re-measure and restore the centre, which Maps loses on a
      size change it was not told about.
    */
    if (window.ResizeObserver) {
      var lastW = widthOf(mapEl);
      var wasHidden = false;
      new window.ResizeObserver(function () {
        var w = widthOf(mapEl);
        /* Switching to Text/Handstil hides the panel; note it and wait for the return. */
        if (w <= 0) { wasHidden = true; return; }
        /* Re-shown at the same width still needs the nudge, hence wasHidden. */
        if (w === lastW && !wasHidden) { return; }
        wasHidden = false;
        lastW = w;
        var c = map.getCenter();
        google.maps.event.trigger(map, 'resize');
        if (c) { map.setCenter(c); }
      }).observe(mapEl);
    }

    /* Seed the field with the default centre, exactly as before. */
    writeCoords(START.lat, START.lng);

    /*
      Click pans instead of dropping the coordinate somewhere the pin is not.
      The coordinate is also written directly here rather than relying on the
      "idle" below: clicking dead centre moves the camera by nothing, so no idle
      would fire — and on a first click that is also the event that lifts
      theme.liquid's write suppression, which would leave the field empty.
    */
    map.addListener('click', function (ev) {
      if (!ev || !ev.latLng) { return; }
      map.panTo(ev.latLng);
      writeCoords(ev.latLng.lat(), ev.latLng.lng());
    });

    /* One write per settled movement, rather than one per animation frame. */
    map.addListener('idle', function () {
      var c = map.getCenter();
      if (c) { writeCoords(c.lat(), c.lng()); }
    });

    var searchEl = document.querySelector('[data-adev-map-search]');
    if (!searchEl || !google.maps.places) { return; }

    var searchBox = new google.maps.places.SearchBox(searchEl);
    map.controls[google.maps.ControlPosition.TOP_LEFT].push(searchEl);

    /* Bias autocomplete to the visible area — the only thing bounds_changed is still used for. */
    map.addListener('bounds_changed', function () {
      searchBox.setBounds(map.getBounds());
    });

    searchBox.addListener('places_changed', function () {
      var places = searchBox.getPlaces();
      if (!places || !places.length) { return; }

      var located = [];
      var i;
      for (i = 0; i < places.length; i++) {
        if (places[i].geometry && places[i].geometry.location) { located.push(places[i]); }
      }
      if (!located.length) { return; }

      if (located.length === 1) {
        /* Zoom to the result's viewport, then centre exactly on the point so the pin is on it. */
        if (located[0].geometry.viewport) { map.fitBounds(located[0].geometry.viewport); }
        map.setCenter(located[0].geometry.location);
        return;
      }

      var bounds = new google.maps.LatLngBounds();
      for (i = 0; i < located.length; i++) {
        if (located[i].geometry.viewport) { bounds.union(located[i].geometry.viewport); }
        else { bounds.extend(located[i].geometry.location); }
      }
      map.fitBounds(bounds);
    });
  }

  function mcMap() {
    var mapEl = document.querySelector('.adev-map');
    if (!mapEl || !window.google || !window.google.maps) { return; }
    /* adev.init() also calls map(); never build twice on the same element. */
    if (mapEl.getAttribute('data-mc-map')) { return; }

    if (widthOf(mapEl) > 0) {
      mapEl.setAttribute('data-mc-map', 'ready');
      build(mapEl);
      return;
    }

    /*
      Hover pre-loaded Maps while the tab is still closed. Park the container back on
      the shimmer placeholder (theme.liquid removed that class when it restored
      .adev-map) so the panel is never a blank box, and build for real the moment the
      customer opens the tab and the container gets a width.
    */
    mapEl.setAttribute('data-mc-map', 'pending');
    mapEl.classList.add('mc-maps-deferred');
    whenSized(mapEl, function () {
      mapEl.classList.remove('mc-maps-deferred');
      mapEl.setAttribute('data-mc-map', 'ready');
      build(mapEl);
    });
  }

  /*
    adev.js is deferred in <head>; this file is deferred in <body>, so adev normally
    exists already. The retry only covers load-order surprises.
  */
  function install() {
    if (!window.adev) { return false; }
    window.adev.map = mcMap;
    return true;
  }

  if (!install()) {
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      if (install() || tries > 100) { window.clearInterval(timer); }
    }, 50);
  }
})();
